/**
 * PublicPlayerCardPage — shareable /player/:playerId
 * No login required. WhatsApp-friendly.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Share2, Trophy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import api from '@/lib/api';
import SportIcon, { SPORT_LABELS } from '../components/SportIcon';

const TIER_COLOR = { platinum: '#f5c518', regular: '#3b82f6' };

function StatBox({ label, value, color }) {
  return (
    <div className="flex flex-col items-center bg-[#161a26] rounded-xl px-4 py-3 border border-[#252a37]">
      <p className="text-2xl font-black tabular-nums" style={{ color }}>{value}</p>
      <p className="text-[10px] text-[#8a93a3] uppercase tracking-widest mt-0.5">{label}</p>
    </div>
  );
}

export default function PublicPlayerCardPage() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/players/${playerId}`)
      .then(r => setPlayer(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [playerId]);

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Player card link copied!'))
      .catch(() => toast.info(url));
  };

  if (loading) return (
    <div className="min-h-screen bg-[#06080d] flex items-center justify-center">
      <RefreshCw className="h-8 w-8 text-[#f5c518] animate-spin"/>
    </div>
  );

  if (!player) return (
    <div className="min-h-screen bg-[#06080d] flex flex-col items-center justify-center gap-4">
      <p className="text-[#8a93a3] text-lg">Player not found</p>
      <button onClick={() => navigate(-1)} className="text-[#f5c518] text-sm hover:underline">← Go back</button>
    </div>
  );

  const stats = player.stats || {};
  const primaryTeam = player.teams?.[0];

  return (
    <div className="min-h-screen bg-[#06080d] text-[#f0f2f7]">
      <Toaster position="top-center"/>

      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#06080d]/90 backdrop-blur border-b border-[#1e2435] px-4 h-12 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="text-xs text-[#8a93a3] hover:text-[#f0f2f7]">← Back</button>
        <p className="text-xs text-[#8a93a3] font-medium">Player Card</p>
        <button onClick={handleShare} className="text-[#f5c518] hover:text-[#f7d24a]">
          <Share2 className="h-4 w-4"/>
        </button>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-5">

        {/* ── Hero card ── */}
        <div className="bg-[#161a26] rounded-2xl border border-[#252a37] overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1.5 w-full" style={{ backgroundColor: primaryTeam?.team_color || '#f5c518' }}/>

          <div className="p-5">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="h-20 w-20 rounded-2xl flex-shrink-0 overflow-hidden flex items-center justify-center font-black text-2xl text-white border-2"
                style={{ backgroundColor: primaryTeam?.team_color || '#3b82f6',
                         borderColor: `${primaryTeam?.team_color || '#3b82f6'}60` }}>
                {player.photo_url
                  ? <img src={player.photo_url} className="h-full w-full object-cover" alt={player.full_name}
                         onError={e=>{e.currentTarget.style.display='none'}}/>
                  : player.full_name?.[0] || '?'}
              </div>

              <div className="min-w-0 flex-1">
                <h1 className="text-xl font-black text-[#f0f2f7] leading-tight">{player.full_name}</h1>
                {primaryTeam && (
                  <p className="text-sm text-[#8a93a3] mt-0.5 truncate">{primaryTeam.team_name}</p>
                )}
                {primaryTeam?.tournament_name && (
                  <p className="text-[10px] text-[#8a93a3]/70 mt-0.5 truncate">{primaryTeam.tournament_name}</p>
                )}
                {/* Badges */}
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  {primaryTeam?.is_captain && (
                    <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-[rgba(245,197,24,0.15)] text-[#f5c518] border border-[rgba(245,197,24,0.3)]">
                      ★ Captain
                    </span>
                  )}
                  {player.is_platinum && (
                    <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-[rgba(245,197,24,0.15)] text-[#f5c518] border border-[rgba(245,197,24,0.3)]">
                      ★ Platinum
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats row ── */}
        {stats.played > 0 && (
          <div className="grid grid-cols-3 gap-3">
            <StatBox label="Played" value={stats.played} color="#f0f2f7"/>
            <StatBox label="Wins"   value={stats.wins}   color="#22c55e"/>
            <StatBox label="Win%"   value={`${stats.win_pct}%`} color="#f5c518"/>
          </div>
        )}

        {/* ── Per-sport breakdown ── */}
        {player.per_sport?.length > 0 && (
          <div className="bg-[#161a26] rounded-2xl border border-[#252a37] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#252a37] bg-[#10131c]">
              <p className="text-xs font-bold text-[#8a93a3] uppercase tracking-widest">Sport Stats</p>
            </div>
            <div className="divide-y divide-[#252a37]/50">
              {player.per_sport.map(ps => {
                const pct = ps.win_pct || 0;
                return (
                  <div key={ps.sport_code} className="px-4 py-3 flex items-center gap-3">
                    <span className="text-xl flex-shrink-0"><SportIcon sport={ps.sport_code} size={36}/></span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#f0f2f7]">{SPORT_LABELS[ps.sport_code] || ps.sport_code}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-[#1d2230] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: pct >= 70 ? '#22c55e' : pct >= 40 ? '#f5c518' : '#ef4444' }}/>
                        </div>
                        <span className="text-xs font-bold tabular-nums" style={{ color: pct >= 70 ? '#22c55e' : pct >= 40 ? '#f5c518' : '#ef4444' }}>
                          {pct}%
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-black text-[#22c55e]">{ps.wins}W</p>
                      <p className="text-[10px] text-[#8a93a3]">{ps.played}P</p>
                    </div>
                    {/* Platinum badge for this sport */}
                    {(player.teams || []).some(t =>
                      (t.platinum_sports || []).includes(ps.sport_code) || t.is_platinum
                    ) && (
                      <span className="text-[9px] font-black text-[#f5c518] bg-[rgba(245,197,24,0.1)] px-1.5 py-0.5 rounded-full border border-[rgba(245,197,24,0.25)] flex-shrink-0">
                        ★
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── No stats yet ── */}
        {!stats.played && (
          <div className="text-center py-10">
            <Trophy className="h-10 w-10 text-[#8a93a3] mx-auto mb-2"/>
            <p className="text-[#8a93a3] text-sm">No matches played yet</p>
            <p className="text-[10px] text-[#8a93a3]/60 mt-1">Stats will appear as matches are scored</p>
          </div>
        )}

        {/* ── Share CTA ── */}
        <button onClick={handleShare}
          className="w-full h-12 rounded-xl bg-[rgba(245,197,24,0.08)] border border-[rgba(245,197,24,0.3)] text-[#f5c518] font-bold text-sm flex items-center justify-center gap-2 hover:bg-[rgba(245,197,24,0.12)] transition-colors">
          <Share2 className="h-4 w-4"/>
          Copy Player Card Link
        </button>

        {/* Footer */}
        <p className="text-center text-[10px] text-[#8a93a3]/40 pb-4">BTCCP Circles Sports League</p>
      </div>
    </div>
  );
}
